import React, { useState, useEffect } from 'react';
import './StaffAttendanceLogViewer.css';

const StaffAttendanceLogViewer = ({ csvUrl }) => {
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    const fetchCsvData = async () => {
      try {
        setLoading(true);
        const response = await fetch(csvUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const csvText = await response.text();
        
        // 備考欄の改行を半角スペースに置換（CSVの行分割前に実行）
        const cleanedCsvText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        const lines = cleanedCsvText.split('\n').filter(line => line.trim() !== '');
        
        if (lines.length > 0) {
          const headers = lines[0].split(',').map(header => header.trim());
          const data = lines.slice(1).map(line => {
            const values = line.split(',').map(value => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              let fieldValue = values[index] || '';
              // 備考欄の場合、改行文字を半角スペースに置換
              if (header === '備考') {
                fieldValue = fieldValue.replace(/\n/g, ' ').replace(/\r/g, ' ');
              }
              row[header] = fieldValue;
            });
            return row;
          });
          
          setHeaders(headers);
          setCsvData(data);
          setFilteredData(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (csvUrl) {
      fetchCsvData();
    }
  }, [csvUrl]);

  useEffect(() => {
    let filtered = csvData;
    
    console.log('フィルタリング開始:', {
      selectedYear,
      selectedMonth,
      selectedStaff,
      totalRecords: csvData.length
    });
    
    if (selectedYear) {
      const yearFilter = selectedYear + '年';
      filtered = filtered.filter(row => 
        (row['日付'] || '').includes(yearFilter)
      );
      console.log(`${selectedYear}年でフィルタリング後:`, filtered.length, '件');
    }
    
    if (selectedMonth) {
      // 月のフィルタリングを修正（例：03 → 3月）
      const monthNumber = parseInt(selectedMonth, 10);
      const beforeFilter = filtered.length;
      filtered = filtered.filter(row => {
        const date = row['日付'] || '';
        const monthMatch = date.match(/(\d{1,2})月/);
        if (monthMatch) {
          return parseInt(monthMatch[1], 10) === monthNumber;
        }
        return false;
      });
      console.log(`${monthNumber}月でフィルタリング後:`, filtered.length, '件 (前: ${beforeFilter}件)');
    }
    
    if (selectedStaff) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(row => 
        (row['氏名'] || '') === selectedStaff
      );
      console.log(`${selectedStaff}でフィルタリング後:`, filtered.length, '件 (前: ${beforeFilter}件)');
    }
    
    console.log('最終フィルタリング結果:', filtered.length, '件');
    setFilteredData(filtered);
    setCurrentPage(1); // フィルター変更時にページをリセット
    calculateSummary(filtered);
  }, [selectedYear, selectedMonth, selectedStaff, csvData]);

  const calculateSummary = (data) => {
    const summary = {};
    let totalWorkHours = 0;
    let totalBreakTime = 0;
    let lateCount = 0;
    let earlyLeaveCount = 0;
    let absenceCount = 0;

    data.forEach(row => {
      const workHours = parseFloat(row['実働'] || '0');
      const breakTime = parseFloat(row['休憩'] || '0');
      const arrivalTime = row['開始時間'] || '';
      const departureTime = row['終了時間'] || '';
      
      if (workHours > 0) {
        totalWorkHours += workHours;
        totalBreakTime += breakTime;
        
        // 遅刻判定（9:30以降の出勤）
        if (arrivalTime && arrivalTime > '09:30') {
          lateCount++;
        }
        
        // 早退判定（17:00前の退勤）
        if (departureTime && departureTime < '17:00' && departureTime !== '') {
          earlyLeaveCount++;
        }
      } else {
        absenceCount++;
      }
    });

    setSummaryStats({
      totalDays: data.length,
      totalWorkHours: totalWorkHours.toFixed(1),
      totalBreakTime: totalBreakTime.toFixed(1),
      averageWorkHours: data.length > 0 ? (totalWorkHours / data.length).toFixed(1) : '0.0',
      lateCount,
      earlyLeaveCount,
      absenceCount,
      attendanceRate: data.length > 0 ? ((data.length - absenceCount) / data.length * 100).toFixed(1) : '0.0'
    });
  };

  const getStaffList = () => {
    const staff = csvData.map(row => row['氏名'] || '').filter(s => s);
    return [...new Set(staff)];
  };

  const getYearList = () => {
    const years = csvData.map(row => {
      const date = row['日付'] || '';
      const yearMatch = date.match(/(\d{4})年/);
      return yearMatch ? yearMatch[1] : '';
    }).filter(y => y);
    const uniqueYears = [...new Set(years)].sort();
    console.log('利用可能な年:', uniqueYears);
    return uniqueYears;
  };

  const getMonthList = () => {
    const months = csvData.map(row => {
      const date = row['日付'] || '';
      const monthMatch = date.match(/(\d{1,2})月/);
      return monthMatch ? monthMatch[1].padStart(2, '0') : '';
    }).filter(m => m);
    const uniqueMonths = [...new Set(months)].sort();
    console.log('利用可能な月:', uniqueMonths);
    return uniqueMonths;
  };



  const getAttendanceStatus = (arrival, departure, workHours) => {
    if (!workHours || workHours === '0') return 'absence';
    if (!arrival && !departure) return 'absence';
    if (!arrival && departure) return 'irregular';
    if (arrival && !departure) return 'incomplete';
    if (arrival > '09:30') return 'late';
    if (departure < '17:00') return 'early';
    return 'normal';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#28a745';
      case 'late': return '#ffc107';
      case 'early': return '#fd7e14';
      case 'incomplete': return '#dc3545';
      case 'irregular': return '#6f42c1';
      case 'absence': return '#6c757d';
      default: return '#333';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'normal': return '正常';
      case 'late': return '遅刻';
      case 'early': return '早退';
      case 'incomplete': return '未完了';
      case 'irregular': return '不正';
      case 'absence': return '欠勤';
      default: return '不明';
    }
  };

  // ページネーション計算
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div className="staff-attendance-loading">スタッフ勤怠データを読み込み中...</div>;
  }

  if (error) {
    return <div className="staff-attendance-error">エラー: {error}</div>;
  }

  return (
    <div className="staff-attendance-viewer">
      <div className="log-header">
        <h2>⏰ スタッフ勤怠ログ</h2>
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="year-select">年:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">全期間</option>
              {getYearList().map((year, index) => (
                <option key={index} value={year}>{year}年</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="month-select">月:</label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">全月</option>
              {getMonthList().map((month, index) => (
                <option key={index} value={month}>{month}月</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="staff-select">スタッフ:</label>
            <select
              id="staff-select"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              <option value="">全スタッフ</option>
              {getStaffList().map((staff, index) => (
                <option key={index} value={staff}>{staff}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

             <div className="summary-dashboard">
         <div className="summary-grid">
           <div className="summary-item total-days">
             <h3>出勤日数</h3>
             <span className="count">{summaryStats.totalDays}日</span>
           </div>
           <div className="summary-item total-hours">
             <h3>総勤務時間</h3>
             <span className="count">{summaryStats.totalWorkHours}時間</span>
           </div>
           <div className="summary-item average-hours">
             <h3>平均勤務時間</h3>
             <span className="count">{summaryStats.averageWorkHours}時間/日</span>
           </div>
         </div>
       </div>

      <div className="attendance-content">
        <div className="attendance-table-container">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>日付</th>
                <th>曜日</th>
                <th>氏名</th>
                <th>開始時間</th>
                <th>終了時間</th>
                <th>休憩</th>
                <th>実働</th>
                <th>備考</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((row, index) => {
                const arrival = row['開始時間'] || '';
                const departure = row['終了時間'] || '';
                const breakTime = row['休憩'] || '';
                const workHours = row['実働'] || '';
                const status = getAttendanceStatus(arrival, departure, workHours);
                
                return (
                  <tr key={index} className={`status-${status}`}>
                    <td className="date-cell">{row['日付'] || '日付不明'}</td>
                    <td className="day-cell">{row['曜日'] || '-'}</td>
                    <td className="staff-cell">{row['氏名'] || '名前不明'}</td>
                    <td className="time-cell">{arrival || '-'}</td>
                    <td className="time-cell">{departure || '-'}</td>
                    <td className="break-cell">{breakTime || '-'}</td>
                    <td className="work-hours-cell">{workHours || '-'}</td>
                    <td className="notes-cell">{row['備考'] || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredData.length === 0 && (
        <div className="no-data">
          <p>選択された条件に該当するデータがありません。</p>
        </div>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            <span>
              {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredData.length)} / {filteredData.length} 件
            </span>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← 前へ
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNumber}
                  className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              次へ →
            </button>
          </div>
        </div>
      )}

      <div className="log-footer">
        <div className="footer-stats">
          <span>📊 総レコード数: {csvData.length}</span>
          <span>🔍 表示中: {filteredData.length}</span>
          <span>👥 スタッフ数: {getStaffList().length}</span>
          <span>📄 ページ: {currentPage} / {totalPages}</span>
        </div>
      </div>
    </div>
  );
};

export default StaffAttendanceLogViewer;